import { useEffect, useState } from "react";
import {
  foodsApi,
  mealLogsApi,
  setsApi,
  type DayLogs,
  type Food,
  type MealSet,
  type User,
} from "../api/client";
import { MealLogEntry } from "../components/MealLogEntry";
import { PageHeader } from "../components/PageHeader";
import { dateTimeLocalToIso, nowDateTimeLocalValue } from "../lib/date";

export function ScheduledMealLogPage({ user }: { user: User }) {
  const initialDateTime = nowDateTimeLocalValue();
  const [foods, setFoods] = useState<Food[]>([]);
  const [sets, setSets] = useState<MealSet[]>([]);
  const [day, setDay] = useState<DayLogs | null>(null);
  const [search, setSearch] = useState("");
  const [selectedFoodId, setSelectedFoodId] = useState<string | null>(null);
  const [draftKcal, setDraftKcal] = useState("");
  const [entryDateTime, setEntryDateTime] = useState(initialDateTime);
  const [editingDateTime, setEditingDateTime] = useState(false);
  const [draftEntryDateTime, setDraftEntryDateTime] = useState(initialDateTime);
  const [savingDraft, setSavingDraft] = useState(false);

  const selectedDate = entryDateTime.slice(0, 10);
  const entryDate = entryDateTime.slice(0, 10);
  const entryTime = entryDateTime.slice(11, 16);
  const totalKcal = day?.total_kcal ?? 0;
  const targetKcal = day?.target_kcal ?? user.daily_target_kcal;
  const remaining = targetKcal - totalKcal;
  const trimmedSearch = search.trim();
  const exactFoodMatch = foods.find(
    (food) => food.name.trim().toLocaleLowerCase() === trimmedSearch.toLocaleLowerCase()
  );
  const selectedFood = foods.find((food) => food.id === selectedFoodId) ?? null;
  const activeFood = selectedFood ?? exactFoodMatch ?? null;
  const hasSearchResults = trimmedSearch.length > 0 && foods.length > 0;
  const showComposer = trimmedSearch.length > 0;
  const canCreateFromSearch = trimmedSearch.length > 0 && !hasSearchResults && Number(draftKcal) > 0 && entryDateTime;
  const canRecordExisting = Boolean(activeFood) && Boolean(entryDateTime);

  const reloadSets = async () => {
    setSets(await setsApi.list());
  };

  const reloadDay = async (date = selectedDate) => {
    setDay(await mealLogsApi.byDate(date));
  };

  useEffect(() => {
    reloadSets().catch(console.error);
    reloadDay().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    foodsApi.list().then(setFoods).catch(console.error);
  }, []);

  useEffect(() => {
    reloadDay(selectedDate).catch(console.error);
  }, [selectedDate]);

  useEffect(() => {
    const q = search.trim();
    if (q.length === 0) {
      foodsApi.list().then(setFoods).catch(console.error);
      return;
    }
    const timer = setTimeout(() => {
      foodsApi.list(q).then(setFoods).catch(console.error);
    }, 200);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (trimmedSearch.length === 0) {
      setSelectedFoodId(null);
      return;
    }

    if (selectedFoodId && !foods.some((food) => food.id === selectedFoodId)) {
      setSelectedFoodId(null);
      return;
    }

    if (!selectedFoodId && exactFoodMatch) {
      setSelectedFoodId(exactFoodMatch.id);
    }
  }, [exactFoodMatch, foods, selectedFoodId, trimmedSearch]);

  const resetComposer = () => {
    setSearch("");
    setSelectedFoodId(null);
    setDraftKcal("");
  };

  const openDateTimeEditor = () => {
    setDraftEntryDateTime(entryDateTime);
    setEditingDateTime(true);
  };

  const closeDateTimeEditor = () => {
    setEditingDateTime(false);
    setDraftEntryDateTime(entryDateTime);
  };

  const saveDateTimeEditor = () => {
    setEntryDateTime(draftEntryDateTime);
    setEditingDateTime(false);
  };

  const onTapFood = async (food: Food) => {
    await mealLogsApi.create({
      eaten_at: dateTimeLocalToIso(entryDateTime),
      food_name: food.name,
      kcal: food.kcal,
      food_id: food.id,
    });
    await reloadDay();
  };

  const onTapSet = async (set: MealSet) => {
    await mealLogsApi.fromSet(set.id, dateTimeLocalToIso(entryDateTime));
    await reloadDay();
  };

  const onDeleteLog = async (id: string) => {
    await mealLogsApi.remove(id);
    await reloadDay();
  };

  const onCreateFoodFromSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreateFromSearch) return;
    setSavingDraft(true);
    try {
      const createdFood = await foodsApi.create({
        name: trimmedSearch,
        kcal: Number(draftKcal),
      });
      await mealLogsApi.create({
        eaten_at: dateTimeLocalToIso(entryDateTime),
        food_name: createdFood.name,
        kcal: createdFood.kcal,
        food_id: createdFood.id,
      });
      resetComposer();
      await reloadDay();
    } finally {
      setSavingDraft(false);
    }
  };

  const onRecordExistingFromSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeFood || !canRecordExisting) return;
    setSavingDraft(true);
    try {
      await mealLogsApi.create({
        eaten_at: dateTimeLocalToIso(entryDateTime),
        food_name: activeFood.name,
        kcal: activeFood.kcal,
        food_id: activeFood.id,
      });
      resetComposer();
      await reloadDay();
    } finally {
      setSavingDraft(false);
    }
  };

  return (
    <div className="page">
      <PageHeader title="指定日の食事を登録" titleClassName="page-title-half" />

      <div className="summary-card">
        <div className="summary-target">対象日の摂取カロリー</div>
        <div className="summary-kcal">{totalKcal} kcal</div>
        <div className="summary-target" style={{ marginTop: 6 }}>
          目標 {targetKcal} kcal / 残り {remaining} kcal
        </div>
      </div>

      <div className="field">
        <label htmlFor="scheduled-meal-datetime-display">登録日時</label>
        <div className="quick-create-time-control">
          <button
            id="scheduled-meal-datetime-display"
            type="button"
            className="quick-create-time-display"
            onClick={openDateTimeEditor}
          >
            {entryDate.replaceAll("-", "/")} {entryTime}
          </button>
        </div>
      </div>

      <div className="field">
        <div className="search-input-row">
          <input
            type="search"
            placeholder="食品を検索・登録"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setSelectedFoodId(null);
            }}
          />
        </div>
      </div>

      {trimmedSearch.length > 0 && foods.length > 0 && !activeFood && (
        <div className="search-suggestion-list" role="listbox" aria-label="食品候補">
          {foods.slice(0, 6).map((food) => (
            <button
              key={food.id}
              type="button"
              className={`search-suggestion-item${selectedFoodId === food.id ? " is-selected" : ""}`}
              onClick={() => {
                setSearch(food.name);
                setSelectedFoodId(food.id);
              }}
            >
              <span className="search-suggestion-name">{food.name}</span>
              <span className="search-suggestion-kcal">{food.kcal} kcal</span>
            </button>
          ))}
        </div>
      )}

      {showComposer && (
        <div className="quick-create-card">
          <div className="quick-create-title">
            {activeFood
              ? "選択した食品を指定日時で記録できます"
              : hasSearchResults
                ? "候補から食品を選択してください"
                : "検索結果にないので新規登録できます"}
          </div>

          {trimmedSearch.length > 0 && <div className="quick-create-name">{trimmedSearch}</div>}

          {!activeFood && !hasSearchResults && (
            <div className="field quick-create-kcal-field" style={{ marginBottom: 0 }}>
              <label htmlFor="scheduled-create-kcal">カロリー (kcal)</label>
              <input
                id="scheduled-create-kcal"
                inputMode="numeric"
                value={draftKcal}
                onChange={(e) => setDraftKcal(e.target.value)}
                placeholder="例: 320"
              />
            </div>
          )}

          {activeFood ? (
            <form onSubmit={onRecordExistingFromSearch}>
              <div className="quick-create-existing-kcal">{activeFood.kcal} kcal</div>
              <button type="submit" className="btn-primary" disabled={!canRecordExisting || savingDraft}>
                {savingDraft ? "記録中…" : "この食品を指定日時で記録"}
              </button>
            </form>
          ) : !hasSearchResults && trimmedSearch.length > 0 ? (
            <form onSubmit={onCreateFoodFromSearch}>
              <button type="submit" className="btn-primary" disabled={!canCreateFromSearch || savingDraft}>
                {savingDraft ? "作成中…" : "新規作成して指定日時で記録"}
              </button>
            </form>
          ) : null}
        </div>
      )}

      {editingDateTime && (
        <div className="modal-backdrop" role="presentation">
          <div className="modal-card" role="dialog" aria-modal="true" aria-labelledby="scheduled-datetime-title">
            <h2 id="scheduled-datetime-title" className="modal-title">
              登録日時を変更
            </h2>
            <div className="field">
              <label htmlFor="scheduled-meal-datetime-editor">日時</label>
              <input
                id="scheduled-meal-datetime-editor"
                type="datetime-local"
                value={draftEntryDateTime}
                onChange={(e) => setDraftEntryDateTime(e.target.value)}
              />
            </div>
            <div className="camera-modal-actions">
              <button type="button" className="btn-secondary" onClick={closeDateTimeEditor}>
                キャンセル
              </button>
              <button type="button" className="btn-primary" onClick={saveDateTimeEditor}>
                反映する
              </button>
            </div>
          </div>
        </div>
      )}

      <h2 className="section-title">よく食べる</h2>
      <div className="grid-2 today-food-grid">
        {foods.slice(0, 8).map((food) => (
          <button key={food.id} className="tile today-food-tile" onClick={() => onTapFood(food)}>
            <div className="tile-name">{food.name}</div>
            <div className="tile-kcal">{food.kcal} kcal</div>
          </button>
        ))}
        {foods.length === 0 && trimmedSearch.length === 0 && (
          <p className="muted" style={{ gridColumn: "1 / -1" }}>
            まだ食品が登録されていません。
          </p>
        )}
        {foods.length === 0 && trimmedSearch.length > 0 && (
          <p className="muted" style={{ gridColumn: "1 / -1" }}>
            一致する食品が見つかりません。
          </p>
        )}
      </div>

      {sets.length > 0 && (
        <>
          <h2 className="section-title">セット</h2>
          <div className="grid-2 today-food-grid">
            {sets.slice(0, 6).map((set) => (
              <button key={set.id} className="tile today-food-tile" onClick={() => onTapSet(set)}>
                <div className="tile-name">{set.name}</div>
                <div className="tile-kcal">{set.total_kcal} kcal</div>
              </button>
            ))}
          </div>
        </>
      )}

      <h2 className="section-title">選択日の記録</h2>
      {day && day.logs.length > 0 ? (
        day.logs.map((log) => (
          <MealLogEntry key={log.id} log={log} onChanged={() => reloadDay()} onDelete={onDeleteLog} />
        ))
      ) : (
        <p className="muted">この日の記録はまだありません。</p>
      )}
    </div>
  );
}